/**
 * AboutStory.tsx — Gold Garage Door / About Us · Section 2/10  (v2 SCROLLYTELLING)
 * Cream · sticky photo right · 4 milestone blocks scroll left · giant year watermark
 * IntersectionObserver lights the active milestone gold; others dim.
 *
 * IMAGE: upload ecb5a9d3-...webp -> about-story-team-carport.webp (1024x768, WebP q82)
 *
 * 10WEB AI: keep <img> as-is. Wave divider at top is intentional — do NOT strip.
 *    IntersectionObserver here is a class toggle ONLY — not a lazy-loader. Keep it.
 */

import { useEffect, useRef, useState } from "react";

// --- IMAGE CONSTANTS — REPLACE AFTER UPLOAD -------------------------------
const STORY_IMG = "/wp-content/uploads/2026/06/about-story-team-carport.webp";
// --------------------------------------------------------------------------

const NAVY = "#081438";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const INK = "#1A1F2E";
const CREAM = "#FAF7F1";
const MILESTONES = [{
  year: "2009",
  head: "One van, one promise",
  text: "We started out of a single van in Bass Hill — one family, one phone number, jobs won purely on word of mouth. Turn up on time, fixed fair price, don't leave until the door works."
}, {
  year: "2014",
  head: "Past a thousand jobs",
  text: "Demand outgrew repairs alone. We added full sectional and roller-door installs while keeping every callout in-house — no sub-contractors who've never met you."
}, {
  year: "2019",
  head: "All of Sydney",
  text: "From the Shire to the Northern Beaches, Western Sydney to the Eastern Suburbs — 200+ suburbs covered by the same crew, same standard, same van."
}, {
  year: "2026",
  head: "Same family, 5.0 stars",
  text: "Fifteen years on, the hands that started this business are still on the tools. Over 1,247 doors fitted and repaired, and a 5.0 rating on Google we protect like family."
}];
function Milestone({
  m,
  active,
  onActive
}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => entries.forEach(e => e.isIntersecting && onActive()), {
      rootMargin: "-45% 0px -45% 0px",
      threshold: 0
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [onActive]);
  return <div ref={ref} style={{
    position: "relative",
    minHeight: "78vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingLeft: "2rem",
    opacity: active ? 1 : 0.32,
    transition: "opacity 0.5s ease"
  }} className="ast-milestone">
      <div aria-hidden style={{
      position: "absolute",
      left: "-0.5rem",
      top: "50%",
      transform: "translateY(-50%)",
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 900,
      fontSize: "clamp(7rem, 16vw, 15rem)",
      lineHeight: 1,
      color: active ? `${GOLD}1A` : `${NAVY}0A`,
      transition: "color 0.5s ease",
      userSelect: "none",
      pointerEvents: "none",
      whiteSpace: "nowrap",
      zIndex: 0
    }}>
        {m.year}
      </div>

      <div aria-hidden style={{
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)"
    }}>
        <div style={{
        width: active ? "18px" : "12px",
        height: active ? "18px" : "12px",
        borderRadius: "50%",
        background: active ? GOLD : "transparent",
        border: `3px solid ${active ? GOLD : `${NAVY}40`}`,
        transition: "all 0.5s ease",
        boxShadow: active ? `0 0 0 6px ${GOLD}22` : "none"
      }} />
      </div>

      <div style={{
      position: "relative",
      zIndex: 1,
      maxWidth: "440px"
    }}>
        <div style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 800,
        fontSize: "0.9rem",
        letterSpacing: "0.1em",
        color: active ? GOLD_DARK : `${INK}80`,
        transition: "color 0.5s ease"
      }}>
          {m.year}
        </div>
        <h3 style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 800,
        fontSize: "clamp(1.4rem, 2.4vw, 1.9rem)",
        color: NAVY,
        lineHeight: 1.15,
        margin: "0.35rem 0 0.75rem"
      }}>
          {m.head}
        </h3>
        <p style={{
        fontFamily: "'Manrope', sans-serif",
        fontSize: "1.02rem",
        color: INK,
        lineHeight: 1.75
      }}>
          {m.text}
        </p>
      </div>
    </div>;
}
export default function AboutStory() {
  const [activeIdx, setActiveIdx] = useState(0);
  return <section id="about-story" style={{
    background: CREAM,
    position: "relative",
    overflow: "hidden",
    paddingBottom: "4rem"
  }}>
      <style>{`
        .ast-grid { display: grid; grid-template-columns: 1fr 0.78fr; gap: 3rem; }
        .ast-photo-sticky { display: block; }
        .ast-photo-mobile { display: none; }
        @media (max-width: 880px) {
          .ast-grid { grid-template-columns: 1fr; gap: 0; }
          .ast-photo-sticky { display: none; }
          .ast-photo-mobile { display: block; }
          .ast-milestone { min-height: auto !important; padding: 2.5rem 0 2.5rem 1.5rem !important; }
          #about-story { padding-bottom: 3rem; }
        }
      `}</style>
      <div aria-hidden style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      lineHeight: 0,
      transform: "rotate(180deg)",
      zIndex: 2
    }}>
        <svg viewBox="0 0 1440 48" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        display: "block",
        width: "100%",
        height: "48px"
      }}>
          <path d="M0,48 C360,0 1080,0 1440,48 L1440,48 L0,48 Z" fill={NAVY} />
        </svg>
      </div>

      <svg aria-hidden style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.4,
      pointerEvents: "none",
      mixBlendMode: "multiply"
    }}>
        <filter id="paper-as">
          <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="5" stitchTiles="stitch" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.85, 0 0 0 0 0.82, 0 0 0 0 0.74, 0 0 0 0.08 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#paper-as)" />
      </svg>

      <div aria-hidden style={{
      position: "absolute",
      top: "5rem",
      left: "2rem",
      width: "60px",
      height: "60px",
      borderTop: `2px solid ${GOLD}55`,
      borderLeft: `2px solid ${GOLD}55`,
      pointerEvents: "none",
      zIndex: 1
    }} />

      <div style={{
      maxWidth: "1150px",
      margin: "0 auto",
      padding: "5rem 1.5rem 0",
      position: "relative",
      zIndex: 1
    }}>
        <span style={{
        fontFamily: "'Manrope', sans-serif",
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.16em",
        color: GOLD_DARK,
        textTransform: "uppercase"
      }}>
          Our Story
        </span>
        <h2 style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 800,
        fontSize: "clamp(1.75rem, 3vw, 2.6rem)",
        color: NAVY,
        lineHeight: 1.15,
        margin: "0.75rem 0 0",
        maxWidth: "620px"
      }}>
          Fifteen years, one driveway at a time
        </h2>

        <p style={{
        fontFamily: "'Manrope', sans-serif",
        fontSize: "1.08rem",
        color: INK,
        lineHeight: 1.8,
        margin: "1.5rem 0 0",
        maxWidth: "620px"
      }}>
          <span style={{
          float: "left",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 800,
          fontSize: "3.6rem",
          lineHeight: 0.8,
          color: GOLD,
          paddingRight: "0.6rem",
          paddingTop: "0.4rem"
        }}>
            I
          </span>
          n 2009 we started with one van, one ladder and a simple promise. No
          call centres, no faceless sub-contractors — just a Sydney family that
          knows garage doors inside out. Here's how the last fifteen years
          unfolded.
        </p>
      </div>

      <div className="ast-grid" style={{
      maxWidth: "1150px",
      margin: "0 auto",
      padding: "0 1.5rem",
      position: "relative",
      zIndex: 1
    }}>
        <div style={{
        position: "relative"
      }}>
          <div aria-hidden style={{
          position: "absolute",
          left: "8px",
          top: "10%",
          bottom: "10%",
          width: "2px",
          background: `linear-gradient(${GOLD}, ${GOLD}25)`
        }} />
          {MILESTONES.map((m, i) => <Milestone key={m.year} m={m} active={activeIdx === i} onActive={() => setActiveIdx(i)} />)}
        </div>

        <div className="ast-photo-sticky">
          <div style={{
          position: "sticky",
          top: "12vh",
          paddingTop: "10vh"
        }}>
            <div style={{
            position: "relative"
          }}>
              <div aria-hidden style={{
              position: "absolute",
              top: "-12px",
              left: "-12px",
              width: "100%",
              height: "100%",
              border: `2px solid ${GOLD}40`,
              borderRadius: "16px",
              pointerEvents: "none"
            }} />
              <img src={STORY_IMG} alt="Gold Garage Door technicians installing a garage door at a Sydney home" width={500} height={620} loading="lazy" decoding="async" style={{
              width: "100%",
              height: "62vh",
              maxHeight: "560px",
              objectFit: "cover",
              borderRadius: "12px",
              display: "block",
              boxShadow: "0 18px 50px rgba(8,20,56,0.18)",
              position: "relative",
              zIndex: 1
            }} src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/ecb5a9d3-0201-404c-b545-d75f7e3bbef0.webp" />
              <div style={{
              position: "absolute",
              bottom: "1rem",
              left: "1rem",
              background: NAVY,
              border: `1px solid ${GOLD}50`,
              borderRadius: "999px",
              padding: "0.5rem 1.1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              zIndex: 2,
              boxShadow: "0 6px 24px rgba(0,0,0,0.4)"
            }}>
                <span style={{
                width: "7px",
                height: "7px",
                borderRadius: "50%",
                background: GOLD,
                display: "inline-block"
              }} />
                <span style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: "0.85rem",
                fontWeight: 800,
                color: GOLD
              }}>
                  {MILESTONES[activeIdx].year}
                </span>
                <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "rgba(255,255,255,0.7)"
              }}>
                  {MILESTONES[activeIdx].head}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ast-photo-mobile" style={{
      maxWidth: "1150px",
      margin: "0 auto",
      padding: "1rem 1.5rem 0"
    }}>
        <img src={STORY_IMG} alt="Gold Garage Door technicians installing a garage door at a Sydney home" width={500} height={360} loading="lazy" decoding="async" style={{
        width: "100%",
        height: "260px",
        objectFit: "cover",
        borderRadius: "12px",
        display: "block",
        boxShadow: "0 12px 40px rgba(8,20,56,0.15)"
      }} />
      </div>

      <div style={{
      maxWidth: "1150px",
      margin: "3rem auto 0",
      padding: "0 1.5rem",
      position: "relative",
      zIndex: 1
    }}>
        <div style={{
        display: "flex",
        gap: "2.5rem",
        flexWrap: "wrap",
        borderTop: `1px solid ${GOLD}30`,
        paddingTop: "1.5rem"
      }}>
          {[{
          n: "15+",
          l: "Years on the tools"
        }, {
          n: "100%",
          l: "Family-operated"
        }, {
          n: "0",
          l: "Call centres"
        }, {
          n: "1,247+",
          l: "Doors fitted & fixed"
        }].map((f, i) => <div key={i}>
              <div style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800,
            fontSize: "1.75rem",
            color: NAVY,
            lineHeight: 1
          }}>{f.n}</div>
              <div style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: "0.78rem",
            color: GOLD_DARK,
            marginTop: "0.25rem",
            fontWeight: 600
          }}>{f.l}</div>
            </div>)}
        </div>
      </div>
    </section>;
}